// Root view — TrailBivvy/App/RootView.swift: five tabs (Map, Route, Tonight, Trip, More), the Safety button
// reachable everywhere, the onboarding cover, and the hard paywall as a full-screen layer above the tabs (iOS S2).

import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { t } from '../i18n';
import { Icon, type IconName } from './components/Icon';
import { useEnv } from './env';
import { OnboardingScreen } from './onboarding/OnboardingScreen';
import { PaywallScreen } from './paywall/PaywallScreen';
import { ProScreen, RestoreScreen, SubscribedScreen } from './paywall/SubscriptionFlows';
import { currentTab, href, route, type Tab } from './router';
import { screens } from './screens';
import { SheetHost } from './sheets';
import { UpdateBar } from './system/UpdateBar';

const tabIcons: Record<Tab, IconName> = { map: 'map', route: 'route', tonight: 'moon', trip: 'hiker', more: 'more' };

function TabBar({ active }: { active: Tab | undefined }) {
  return (
    <nav class="tabbar" aria-label="TrailBivvy">
      {(Object.keys(tabIcons) as Tab[]).map((tab) => (
        <a key={tab} href={href(tab)} aria-current={active === tab ? 'page' : undefined} id={`tab-${tab}`}>
          <Icon name={tabIcons[tab]} />
          <span>{t(`tab.${tab}`)}</span>
        </a>
      ))}
    </nav>
  );
}

function OfflineBar() {
  const env = useEnv();
  if (env.isOnline.value) return null;
  return (
    <div class="offline-bar" role="status">
      {t('pwa.offline.banner')}
    </div>
  );
}

/** Screens that belong to the purchase flows: shown even above the onboarding and the paywall. */
function flowScreen(path: string[]): ComponentChildren | undefined {
  if (path[0] === 'subscribed') return <SubscribedScreen />;
  if (path[0] === 'restore') return <RestoreScreen />;
  return undefined;
}

export function App() {
  const env = useEnv();
  const r = route.value;
  // #/safety (manifest shortcut) opens the Safety sheet over whatever is below.
  useEffect(() => {
    if (r.path[0] === 'safety') env.safetyOpen.value = true;
  }, [r]);
  const flow = flowScreen(r.path);
  if (flow) {
    return (
      <div class="app-frame">
        <div class="app-content">{flow}</div>
        {env.safetyOpen.value && screens.safety()}
      </div>
    );
  }

  const active = currentTab(r);
  const paywall = env.paywall.value;
  const showOnboarding = !env.prefs.onboardingCompleted.value;
  const content = r.path[0] === 'more' && r.path[1] === 'pro' ? <ProScreen /> : screens.forRoute(r);

  return (
    <div class="app-frame">
      <UpdateBar />
      <OfflineBar />
      <div class="app-content" aria-hidden={paywall || showOnboarding ? true : undefined}>
        {content}
      </div>
      <TabBar active={active} />
      {paywall && !showOnboarding && <PaywallScreen phase={paywall} />}
      {showOnboarding && <OnboardingScreen />}
      {env.sheet.value && <SheetHost sheet={env.sheet.value} />}
      {env.safetyOpen.value && screens.safety()}
      {env.toast.value && (
        <div class="toast" role="status">
          {env.toast.value}
        </div>
      )}
    </div>
  );
}
